<template>
	<div class="px-10 pt-7">
		<!-- Row 1: Categories + View toggle (desktop) -->
		<div class="flex items-center justify-between gap-3 mb-3">
			<!-- Category chips -->
			<div class="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto scrollbar-hide">
				<span
					class="text-[11px] font-semibold tracking-widest uppercase flex-shrink-0"
					:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
					{{ $t('filter.categoryLabel') }}
				</span>

				<button
					v-for="cat in translatedCategories"
					:key="cat.key"
					@click="$emit('category-change', cat.originalLabel)"
					class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-sm flex-shrink-0 transition-all duration-200 whitespace-nowrap"
					:class="
						activeCategory === cat.originalLabel
							? isLight
								? 'border-[#1a9b8a] bg-[#1a9b8a]/12 text-[#0d6b5e] font-semibold'
								: 'border-[#1a9b8a] bg-[#1a9b8a]/15 text-[#a8d5cc] font-semibold'
							: isLight
							? 'border-[#d0ede8] bg-[#e4f4f0]/50 text-[#0d6b5e] hover:bg-[#d0ede8] hover:border-[#1a9b8a]/40'
							: 'border-white/10 bg-white/5 text-[#a8d5cc] hover:bg-white/8 hover:border-[#1a9b8a]/30'
					">
					<span class="text-sm">{{ cat.icon }}</span>
					{{ cat.label }}
				</button>
			</div>

			<!-- View toggle — desktop only -->
			<div
				class="hidden md:flex gap-0.5 p-1 rounded-[10px] border flex-shrink-0"
				:class="
					isLight
						? 'bg-[#e4f4f0]/80 border-[#d0ede8]'
						: 'bg-white/6 border-white/10'
				">
				<button
					v-for="v in translatedViews"
					:key="v.key"
					@click="$emit('view-change', v.key)"
					class="flex items-center gap-1.5 px-3.5 py-1.5 rounded-[7px] text-sm font-medium transition-all duration-200 whitespace-nowrap"
					:class="
						activeView === v.key
							? isLight
								? 'bg-white text-[#0d6b5e] shadow-sm'
								: 'bg-white/10 text-white'
							: isLight
							? 'text-[#6b8f89] hover:text-[#0f1f1c]'
							: 'text-[#a8d5cc] hover:text-white'
					">
					<component :is="'svg'" v-bind="v.svgAttrs" v-html="v.svgBody"></component>
					{{ v.label }}
				</button>
			</div>
		</div>

		<!-- Row 2: Time chips -->
		<div class="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-0.5">
			<span
				class="text-[11px] font-semibold tracking-widest uppercase flex-shrink-0"
				:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
				{{ $t('filter.whenLabel') }}
			</span>
			<button
				v-for="t in translatedTimes"
				:key="t.key"
				@click="onTimeClick(t.key)"
				class="flex items-center px-4 py-1.5 rounded-full border text-sm font-medium flex-shrink-0 transition-all duration-200 whitespace-nowrap"
				:class="
					activeTime === t.key
						? isLight
							? 'bg-[#0f1f1c] text-white border-[#0f1f1c]'
							: 'bg-[#e4f4f0] text-[#0f1f1c] border-[#e4f4f0]'
						: isLight
						? 'border-[#d0ede8] text-[#6b8f89] hover:border-[#1a9b8a]/40 hover:text-[#0d6b5e]'
						: 'border-white/10 text-[#a8d5cc] hover:border-[#1a9b8a]/30 hover:text-white'
				">
				<span
					v-if="t.key === 'Happening Now'"
					class="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-1.5 animate-pulseFast"></span>
				{{ t.label }}
			</button>
		</div>

		<!-- Custom Date Picker -->
		<div
			v-if="activeTime === 'Pick Date ›'"
			class="mt-4 relative"
			ref="calendarWrapper">

			<div class="flex items-center gap-3">
				<span
					class="text-[11px] font-semibold tracking-widest uppercase flex-shrink-0"
					:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
					Date
				</span>

				<button
					@click="calendarOpen = !calendarOpen"
					class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-medium transition-all duration-200"
					:class="
						pickedDate
							? isLight
								? 'border-[#1a9b8a] bg-[#1a9b8a]/10 text-[#0d6b5e]'
								: 'border-[#1a9b8a] bg-[#1a9b8a]/15 text-[#a8d5cc]'
							: isLight
							? 'border-[#d0ede8] bg-[#e4f4f0]/50 text-[#6b8f89] hover:border-[#1a9b8a]/40 hover:text-[#0d6b5e]'
							: 'border-white/10 bg-white/5 text-[#a8d5cc] hover:border-[#1a9b8a]/30'
					">
					<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
						<line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
						<line x1="3" y1="10" x2="21" y2="10"/>
					</svg>
					{{ pickedDate ? formatSelectedDate(pickedDate) : $t('filter.chooseDate') }}
					<svg
						width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
						class="transition-transform duration-200"
						:class="calendarOpen ? 'rotate-180' : ''">
						<polyline points="6 9 12 15 18 9"/>
					</svg>
				</button>

				<button
					v-if="pickedDate"
					@click="clearDate"
					class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-all duration-200"
					:class="
						isLight
							? 'border-[#d0ede8] text-[#6b8f89] hover:border-[#1a9b8a]/40 hover:text-[#0d6b5e]'
							: 'border-white/10 text-[#a8d5cc] hover:bg-white/8'
					">
					<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
						<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
					</svg>
					{{ $t('filter.clear') }}
				</button>
			</div>

			<!-- Calendar dropdown -->
			<Transition
				enter-active-class="transition-all duration-200 ease-out"
				enter-from-class="opacity-0 translate-y-[-8px]"
				enter-to-class="opacity-100 translate-y-0"
				leave-active-class="transition-all duration-150 ease-in"
				leave-from-class="opacity-100 translate-y-0"
				leave-to-class="opacity-0 translate-y-[-8px]">
				<div
					v-if="calendarOpen"
					class="absolute top-10 left-0 z-[9999] mt-1 rounded-2xl border p-4 w-[300px]"
					:class="
						isLight
							? 'bg-white border-[#d0ede8]'
							: 'bg-[#0f1f1c] border-white/10'
					"
					style="box-shadow: 0 8px 32px rgba(0,0,0,0.12);">

					<!-- Month navigation -->
					<div class="flex items-center justify-between mb-4">
						<button @click="prevMonth" class="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200"
							:class="isLight ? 'text-[#0d6b5e] hover:bg-[#e4f4f0]' : 'text-[#a8d5cc] hover:bg-white/10'">
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
								<polyline points="15 18 9 12 15 6"/>
							</svg>
						</button>
						<span class="text-sm font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
							{{ monthLabel }}
						</span>
						<button @click="nextMonth" class="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200"
							:class="isLight ? 'text-[#0d6b5e] hover:bg-[#e4f4f0]' : 'text-[#a8d5cc] hover:bg-white/10'">
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
								<polyline points="9 18 15 12 9 6"/>
							</svg>
						</button>
					</div>

					<!-- Day of week headers -->
					<div class="grid grid-cols-7 mb-1">
						<span
							v-for="d in translatedDays"
							:key="d"
							class="text-center text-[10px] font-semibold tracking-wider uppercase py-1"
							:class="isLight ? 'text-[#6b8f89]' : 'text-[#a8d5cc]/60'">
							{{ d }}
						</span>
					</div>

					<!-- Day grid -->
					<div class="grid grid-cols-7 gap-y-0.5">
						<span v-for="n in leadingBlanks" :key="'blank-' + n"></span>
						<button
							v-for="day in daysInMonth"
							:key="day"
							@click="selectDay(day)"
							:disabled="isPast(day)"
							class="h-9 w-full rounded-full text-sm font-medium transition-all duration-200 flex items-center justify-center"
							:class="getDayClass(day)">
							{{ day }}
						</button>
					</div>

					<!-- Footer -->
					<div
						class="mt-3 pt-3 border-t flex justify-between items-center"
						:class="isLight ? 'border-[#e4f4f0]' : 'border-white/8'">
						<button
							@click="selectToday"
							class="text-xs font-semibold transition-all duration-200"
							:class="isLight ? 'text-[#1a9b8a] hover:text-[#0d6b5e]' : 'text-[#a8d5cc] hover:text-white'">
							{{ $t('filter.jumpToday') }}
						</button>
						<button
							v-if="pickedDate"
							@click="calendarOpen = false"
							class="text-xs font-semibold px-3 py-1 rounded-full transition-all duration-200"
							:class="
								isLight
									? 'bg-[#0f1f1c] text-white'
									: 'bg-[#e4f4f0] text-[#0f1f1c]'
							">
							{{ $t('filter.done') }}
						</button>
					</div>
				</div>
			</Transition>
		</div>

		<!-- Clear Filters -->
		<div v-if="hasActiveFilters" class="flex items-center mt-4">
			<button
				@click="$emit('reset-filters')"
				class="px-4 py-2 rounded-full border text-sm font-medium transition-all duration-200"
				:class="
					isLight
						? 'border-[#d0ede8] text-[#0d6b5e] hover:bg-[#d0ede8]'
						: 'border-white/10 text-[#a8d5cc] hover:bg-white/10 hover:text-white'
				">
				{{ $t('filter.clearFilters') }}
			</button>
		</div>

		<!-- Row 3: View toggle — mobile only -->
		<div class="flex md:hidden mt-3">
			<div
				class="flex gap-0.5 p-1 rounded-[10px] border"
				:class="
					isLight
						? 'bg-[#e4f4f0]/80 border-[#d0ede8]'
						: 'bg-white/6 border-white/10'
				">
				<button
					v-for="v in translatedViews"
					:key="v.key"
					@click="$emit('view-change', v.key)"
					class="flex items-center gap-1.5 px-3.5 py-1.5 rounded-[7px] text-sm font-medium transition-all duration-200 whitespace-nowrap"
					:class="
						activeView === v.key
							? isLight
								? 'bg-white text-[#0d6b5e] shadow-sm'
								: 'bg-white/10 text-white'
							: isLight
							? 'text-[#6b8f89] hover:text-[#0f1f1c]'
							: 'text-[#a8d5cc] hover:text-white'
					">
					<component :is="'svg'" v-bind="v.svgAttrs" v-html="v.svgBody"></component>
					{{ v.label }}
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import apiService from "@/services/api";
export default {
	name: "FilterBarComponent",
	props: {
		isLight:          Boolean,
		activeCategory:   String,
		activeTime:       String,
		activeView:       String,
		pickedDate:       String,
		hasActiveFilters: Boolean,
	},
	emits: ["category-change", "time-change", "view-change", "date-change", "reset-filters"],
	data() {
		const now = new Date();
		return {
			calendarOpen:  false,
			calendarYear:  now.getFullYear(),
			calendarMonth: now.getMonth(),
			categoryDefs: [],

			// Time chip keys — these must stay as English originals because
			// DiscoverView uses them for date filtering logic.
			timeDefs: [
				{ key: "Happening Now",  i18nKey: "filter.times.happeningNow" },
				{ key: "Today",          i18nKey: "filter.times.today" },
				{ key: "Tomorrow",       i18nKey: "filter.times.tomorrow" },
				{ key: "This Weekend",   i18nKey: "filter.times.thisWeekend" },
				{ key: "Next Week",      i18nKey: "filter.times.nextWeek" },
				{ key: "This Month",     i18nKey: "filter.times.thisMonth" },
				{ key: "Pick Date ›",   i18nKey: "filter.times.pickDate" },
			],

			viewDefs: [
				{
					key: "map",
					i18nKey: "filter.views.map",
					svgAttrs: { width: "13", height: "13", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2" },
					svgBody: '<polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>',
				},
				{
					key: "list",
					i18nKey: "filter.views.list",
					svgAttrs: { width: "13", height: "13", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2" },
					svgBody: '<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>',
				},
			],
		};
	},

	computed: {
		translatedCategories() {
			return this.categoryDefs.map((cat) => ({
				...cat,
				label: this.$t(`${cat.name}`),
			}));
		},
		translatedTimes() {
			return this.timeDefs.map((t) => ({
				...t,
				label: this.$t(t.i18nKey),
			}));
		},
		translatedViews() {
			return this.viewDefs.map((v) => ({
				...v,
				label: this.$t(v.i18nKey),
			}));
		},
		translatedDays() {
			return [
				this.$t('filter.days.su'),
				this.$t('filter.days.mo'),
				this.$t('filter.days.tu'),
				this.$t('filter.days.we'),
				this.$t('filter.days.th'),
				this.$t('filter.days.fr'),
				this.$t('filter.days.sa'),
			];
		},
		monthLabel() {
			return new Date(this.calendarYear, this.calendarMonth, 1)
				.toLocaleString(this.$i18n.locale === 'it' ? 'it-IT' : 'en-GB', { month: "long", year: "numeric" });
		},
		daysInMonth() {
			return new Date(this.calendarYear, this.calendarMonth + 1, 0).getDate();
		},
		leadingBlanks() {
			return new Date(this.calendarYear, this.calendarMonth, 1).getDay();
		},
		selectedDay() {
			if (!this.pickedDate) return null;
			const [y, m, d] = this.pickedDate.split("-").map(Number);
			if (y === this.calendarYear && m - 1 === this.calendarMonth) return d;
			return null;
		},
	},

	watch: {
		activeTime(val) {
			if (val === "Pick Date ›") {
				this.$nextTick(() => { this.calendarOpen = true; });
			} else {
				this.calendarOpen = false;
			}
		},
	},

	mounted() {
		document.addEventListener("click", this.onOutsideClick);
	},
	beforeUnmount() {
		document.removeEventListener("click", this.onOutsideClick);
	},

	methods: {
		onTimeClick(key) {
			this.$emit("time-change", key);
		},
		prevMonth() {
			if (this.calendarMonth === 0) { this.calendarMonth = 11; this.calendarYear--; }
			else { this.calendarMonth--; }
		},
		nextMonth() {
			if (this.calendarMonth === 11) { this.calendarMonth = 0; this.calendarYear++; }
			else { this.calendarMonth++; }
		},
		selectDay(day) {
			if (this.isPast(day)) return;
			const m = String(this.calendarMonth + 1).padStart(2, "0");
			const d = String(day).padStart(2, "0");
			this.$emit("date-change", `${this.calendarYear}-${m}-${d}`);
		},
		selectToday() {
			const now = new Date();
			this.calendarYear  = now.getFullYear();
			this.calendarMonth = now.getMonth();
			const m = String(now.getMonth() + 1).padStart(2, "0");
			const d = String(now.getDate()).padStart(2, "0");
			this.$emit("date-change", `${now.getFullYear()}-${m}-${d}`);
		},
		clearDate() {
			this.$emit("date-change", null);
			this.calendarOpen = false;
		},
		isPast(day) {
			const now   = new Date();
			const cell  = new Date(this.calendarYear, this.calendarMonth, day);
			const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
			return cell < today;
		},
		isToday(day) {
			const now = new Date();
			return day === now.getDate() && this.calendarMonth === now.getMonth() && this.calendarYear === now.getFullYear();
		},
		getDayClass(day) {
			const past     = this.isPast(day);
			const today    = this.isToday(day);
			const selected = this.selectedDay === day;
			if (past)     return this.isLight ? "text-[#c0d4d0] cursor-not-allowed" : "text-white/20 cursor-not-allowed";
			if (selected) return this.isLight ? "bg-[#0f1f1c] text-white" : "bg-[#e4f4f0] text-[#0f1f1c]";
			if (today)    return this.isLight ? "border border-[#1a9b8a] text-[#0d6b5e] font-semibold" : "border border-[#1a9b8a]/60 text-[#a8d5cc] font-semibold";
			return this.isLight ? "text-[#0f1f1c] hover:bg-[#e4f4f0]" : "text-white hover:bg-white/10";
		},
		formatSelectedDate(dateStr) {
			if (!dateStr) return "";
			const [y, m, d] = dateStr.split("-").map(Number);
			return new Date(y, m - 1, d).toLocaleDateString(
				this.$i18n.locale === 'it' ? 'it-IT' : 'en-GB',
				{ weekday: "short", month: "short", day: "numeric" }
			);
		},
		onOutsideClick(e) {
			if (this.$refs.calendarWrapper && !this.$refs.calendarWrapper.contains(e.target)) {
				this.calendarOpen = false;
			}
		},
		async getCategories() {
			try {
				const response = await apiService.getCategories();
				console.log("Fetched categories:", response.data);
				// this.categoryDefs = response.data
				this.categoryDefs = [
					{
						icon: "✨",
						key: "all",
						name: "All",
						originalLabel: "All",
					},
					...response.data.map((category) => ({
						// icon: category.name || "📌",
						key: category.name.toLowerCase().replace(/\s+/g, "-"),
						name: category.name,
						originalLabel: category.name,
					})),
				];
				
			} catch (error) {
				console.error("Failed to fetch categories:", error);
			}
		}
	},
	created() {
		console.log("Fetched categories:", this.categoryDefs);
		this.getCategories();
	},
};
</script>

<style scoped>
.scrollbar-hide { scrollbar-width: none; }
.scrollbar-hide::-webkit-scrollbar { display: none; }
</style>